import React from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

interface ListOptions<T> {
  items: T[];
  getKey: (item: T) => string;
  defaultSelectedKey?: string;
  selectedKey?: string;
  enableAutoSelect?: boolean;
  onChange?: (item: T) => void;
}

export interface ListState<T> {
  items: T[];
  focusedKey: string;
  selectedKey: string;
  setFocusedKey: (key: string) => void;
  setSelectedKey: (key: string) => void;
  getKeys: () => string[];
  getSelectedItem: () => T | undefined;
}

export function useListState<T>(options: ListOptions<T>): ListState<T> {
  const { items, getKey, defaultSelectedKey, selectedKey: selectedKeyProp, onChange } = options;

  const [internalSelectedKey, setInternalSelectedKey] = React.useState<string>(defaultSelectedKey!);

  const selectedKey = selectedKeyProp !== undefined ? selectedKeyProp : internalSelectedKey;

  const [focusedKey, setFocusedKey] = React.useState<string>(selectedKey);

  const getKeys = () => {
    return items.map(item => getKey(item));
  };

  const getSelectedItem = () => {
    return items.find(item => getKey(item) === selectedKey);
  };

  const setSelectedKey = (newKey: string) => {
    setInternalSelectedKey(newKey);
    if (onChange && newKey !== selectedKey) {
      const newItem = items.find(item => getKey(item) === newKey);
      onChange(newItem!);
    }
  };

  return {
    items,
    focusedKey,
    selectedKey,
    getKeys,
    getSelectedItem,
    setFocusedKey,
    setSelectedKey,
  };
}

/**
 * useInlineListState is used mainly for inline category extensions, where items
 * could be dynamically changed and need to be synced up with currently selected item.
 */

export function useInlineListState<T>(options: ListOptions<T>): ListState<T> {
  const { items, getKey, defaultSelectedKey, selectedKey: selectedKeyProp, onChange } = options;

  const [internalItems, setInternalItems] = React.useState(items); // intermediate state to sync updated items and key
  const [internalSelectedKey, setInternalSelectedKey] = React.useState<string>(defaultSelectedKey!);

  const selectedKey = selectedKeyProp !== undefined ? selectedKeyProp : internalSelectedKey;

  const [focusedKey, setFocusedKey] = React.useState<string>(selectedKey);

  const isMounted = React.useRef(false);

  useDeepCompareEffect(() => {
    /**
     * update internal state if items or selected key has been changed
     */
    if (isMounted.current) {
      if (defaultSelectedKey) {
        setInternalSelectedKey(defaultSelectedKey);
        const selectedKey = selectedKeyProp !== undefined ? selectedKeyProp : internalSelectedKey;
        setFocusedKey(selectedKey);
      }
    } else {
      isMounted.current = true;
    }
  }, [defaultSelectedKey, items]);

  useDeepCompareEffect(() => {
    setInternalItems(items);
  }, [items]);

  const getKeys = () => {
    return internalItems.map(item => getKey(item));
  };

  const getSelectedItem = () => {
    return internalItems.find(item => getKey(item) === selectedKey);
  };

  const setSelectedKey = (newKey: string) => {
    setInternalSelectedKey(newKey);
    if (onChange && newKey !== selectedKey) {
      const newItem = internalItems.find(item => getKey(item) === newKey);
      onChange(newItem!);
    }
  };

  return {
    items,
    focusedKey,
    selectedKey,
    getKeys,
    getSelectedItem,
    setFocusedKey,
    setSelectedKey,
  };
}
